import {
  AnalyticsContentType,
  FullscreenTypeEnum,
  type Content,
} from '@setplex/tria-api'
import { LiveChannel } from '~/entities/channels'
import { type FormattedContent } from '~/entities/content'
import { type FormattedEpisode } from '~/entities/tvshows/episodes'
import { IChannelInfo } from '~/widgets/epg/player'
import type { CurrentProgram, IInfoPopupData } from '../types'

const IOS_UA_RE = /iPhone|iPod|iPad/i

export function isIosUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false
  return IOS_UA_RE.test(navigator.userAgent)
}

export type PlayerAnalyticsFullscreenContext = 'epg' | 'page'

/** Resolves analytics `fullscreenType` from player fullscreen state and surface (EPG widget vs full page). */
export function getAnalyticsFullscreenType(
  isFullscreen: boolean,
  context: PlayerAnalyticsFullscreenContext
): FullscreenTypeEnum {
  if (!isFullscreen) {
    return context === 'epg'
      ? FullscreenTypeEnum.minimizedPlayer
      : FullscreenTypeEnum.pageFullscreen
  }
  if (isIosUserAgent()) {
    return FullscreenTypeEnum.nativeFullscreen
  }
  return FullscreenTypeEnum.fullscreenPlayer
}

/**
 * Gets navigation information for a playlist of live channels (navigation is looped)
 *
 * @param {Object} params - The parameters object
 * @param {LiveChannel[]} params.playlist - Array of live channels
 * @param {IChannelInfo | null} params.channelInfo - Information about the current channel including index and count
 *
 * @returns {Object | null} Navigation info object containing next and previous channels, or null if playlist or channelInfo is invalid
 * @returns {LiveChannel | null} nextChannel - The next channel in the playlist
 * @returns {LiveChannel | null} previousChannel - The previous channel in the playlist
 */

export const getPlaylistNavigationInfo = ({
  playlist,
  channelInfo,
}: {
  playlist: LiveChannel[]
  channelInfo: IChannelInfo | null
}): {
  nextChannel: LiveChannel | null
  previousChannel: LiveChannel | null
} | null => {
  if (!playlist?.length || !channelInfo) return null

  const { currentIndex, channelsCount } = channelInfo

  const isFirstOrRemovedFromFavoriteIndex =
    currentIndex === 0 || currentIndex === -1 // -1 when channel was removed from favorites
  const isLastIndex = currentIndex === channelsCount - 1

  const nextIndex = isLastIndex ? 0 : currentIndex + 1
  const prevIndex = isFirstOrRemovedFromFavoriteIndex
    ? channelsCount - 1
    : currentIndex - 1

  const nextChannel = playlist[nextIndex]
  const previousChannel = playlist[prevIndex]

  return {
    nextChannel,
    previousChannel,
  }
}

const formatDescriptionslist = (
  content: Content | FormattedContent
): Record<string, string> => {
  return {
    'Crew': content.directors || '',
    'Cast': content.stars || '',
    'Published by': content.creator?.name || '',
    'Genres': content.tags?.map((tag) => tag.title).join(', ') || '',
    'Tags': content.newOtherTags?.map((tag) => tag.title).join(', ') || '',
    'Country': content.productionCountries?.[0]?.name || '',
    'Languages':
      content.collections?.map((collection) => collection.title).join(', ') ||
      '',
    'Age Rating': content.ageRating
      ? `${content.ageRating}${content.ageRatingReasons?.length ? ` (${content.ageRatingReasons.join(', ')})` : ''}`
      : '',
  }
}

/**
 * Formats channel into the info popup data structure
 *
 * @param {LiveChannel} channel - The channel to format
 * @returns {IInfoPopupData} Formatted content data for info popup
 */
export const formatChannelToInfoPopupData = (
  channel: LiveChannel | null
): IInfoPopupData | null => {
  if (!channel) return null

  return {
    id: channel.id, // id used not in popup
    logo: channel.logo, // logo used not in popup
    title: channel.title,
    labels: [
      channel.categories?.[0]?.title || '',
      channel.collections?.[0]?.title || '',
      channel.productionCountries?.[0]?.name || '',
      channel.ageRating || '',
    ].filter(Boolean),
    description: channel.description || '',
    descriptionList: null,
  }
}

/**
 * Formats content into the info popup data structure
 *
 * @param {Content} content - The content to format
 * @returns {IInfoPopupData} Formatted content data for info popup
 */
export const formatContentToInfoPopupData = (
  content: Content | FormattedContent | null
): IInfoPopupData | null => {
  if (!content) return null

  return {
    id: content.id,
    title: content.title,
    labels: [
      content.releaseYear?.toString() || '',
      content.tags?.[0]?.title || '',
      content.collections?.[0]?.title || '',
      content.productionCountries?.[0]?.name || '',
      content.duration || '',
      content.ageRating || '',
    ].filter(Boolean),
    userRating: content.userRating?.toString() || '',
    description: content.description,
    descriptionList: formatDescriptionslist(content),
  }
}

/**
 * Formats episode into the info popup data structure
 *
 * @param {FormattedEpisode} episode - The episode to format
 * @returns {IInfoPopupData} Formatted episode data for info popup
 */
export const formatEpisodeToInfoPopupData = (
  episode: FormattedEpisode | null
): IInfoPopupData | null => {
  if (!episode) return null

  return {
    id: episode.id,
    title: episode.title,
    labels: [
      episode.code || '',
      episode.duration || '',
      episode.ageRating || '',
    ].filter(Boolean),
    ratingReasons: episode.ratingReasons || [],
    description: episode.description,
    descriptionList: formatDescriptionslist({
      directors: episode.directors,
      stars: episode.stars,
      ageRating: episode.ageRating || '',
      ageRatingReasons: episode.ratingReasons || [],
    } as unknown as Content),
  }
}

export const getContentTypeFromCurrentProgram = (
  currentProgram: CurrentProgram
) => {
  if (currentProgram?.isRewinded) {
    // should be checked before live
    return AnalyticsContentType.liveRewind
  }
  if (currentProgram?.isLive) {
    return AnalyticsContentType.live
  }
  if (currentProgram?.isCatchup) {
    return AnalyticsContentType.catchup
  }
  return ''
}
